import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import React from "react";
import { Box } from "@mui/system";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TableSortLabel,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import "../Maps.css";
import { useDispatch, useSelector } from "react-redux";
import { setSort } from "../../../../../features/mapsSlice";
import { useSearchParams } from "react-router-dom";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import { selectUserToken } from "../../../../../features/userSlice";
import store from "../../../../../store";

interface MapsInsightExpressionModalProps {
  open: boolean;
  expressionHandleClose: () => void;
  expressionTableHeadings: { [key: string]: string };
}

const MapsInsightExpressionModal = ({
  open,
  expressionHandleClose,
}: MapsInsightExpressionModalProps) => {
  const dispatch = useDispatch();
  const fieldResource = useSelector((state: any) => state.maps.fieldResource);
  const { sort } = useSelector((state: any) => state.maps);
  const [searchParams] = useSearchParams();
  const sourceFieldExpressionParam = searchParams.get(
    "source_field_expression",
  );
  const fieldIDParam = searchParams.get("field_id");
  const tableHeaders = [
    { id: 0, KEY: "brand_id", label: "Brand ID & Name", sortable: true },
    { id: 1, KEY: "report_name", label: "Report Name", sortable: true },
    { id: 2, KEY: "report_id", label: "Report ID", sortable: true },
    { id: 3, KEY: "raw_table_id", label: "Raw Table", sortable: true },
    { id: 4, KEY: "config_name", label: "Config Name", sortable: true },
  ];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = React.useState<string | number | null>(
    null,
  );

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Default sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  const sortedRows = getSortedData(fieldResource);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const convertToCSV = (data: any[]) => {
    if (!Array.isArray(data) || data.length === 0) return "";
    const keys = Array.from(
      data.reduce((acc: Set<string>, item: any) => {
        Object.keys(item || {}).forEach((k) => acc.add(k));
        return acc;
      }, new Set<string>()),
    );
    const header = keys.join(",");
    const rows = data.map((row) =>
      keys
        .map((k) => {
          const val =
            row[k] === null || row[k] === undefined ? "" : String(row[k]);
          // escape quotes and wrap in quotes if comma/newline present
          const escaped = val.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(","),
    );
    return [header, ...rows].join("\r\n");
  };

  // ---------- Download helpers ----------
  const openMenu = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string | number,
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  // Create a basic HTML table string (works with application/vnd.ms-excel)
  const convertToHtmlTable = (data: any[]) => {
    if (!Array.isArray(data) || data.length === 0) return "<table></table>";
    const keys = Array.from(
      data.reduce((acc: Set<string>, item: any) => {
        Object.keys(item || {}).forEach((k) => acc.add(k));
        return acc;
      }, new Set<string>()),
    );
    const headerRow = `<tr>${keys.map((k) => `<th>${k}</th>`).join("")}</tr>`;
    const bodyRows = data
      .map(
        (row) =>
          `<tr>${keys
            .map((k) => {
              const v =
                row[k] === null || row[k] === undefined ? "" : String(row[k]);
              // basic escaping
              return `<td>${v
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")}</td>`;
            })
            .join("")}</tr>`,
      )
      .join("");
    return `<table>${headerRow}${bodyRows}</table>`;
  };
  const [, /*laoding*/ setLoading] = React.useState(false);
  const fetchAndDownload = async (rawTableId: any, format: any) => {
    setLoading(true);
    try {
      await MapsEndpointLogic.unifierRawTableData(oauthTokenHeader, rawTableId);

      // Add a small delay to ensure Redux store is updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now use the updated data from store
      const currentState = store.getState(); // You'll need to import store
      const rawTableData = currentState.maps.unifierRawTableData;
      let blob: Blob;
      let filenameBase = `raw_table_${rawTableId}_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}`;

      if (format === "csv") {
        const csv = Array.isArray(rawTableData)
          ? convertToCSV(rawTableData)
          : String(rawTableData);
        blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const filename = `${filenameBase}.csv`;
        triggerBrowserDownload(blob, filename);
      } else if (format === "xls") {
        const html = Array.isArray(rawTableData)
          ? convertToHtmlTable(rawTableData)
          : `<pre>${String(rawTableData)}</pre>`;
        blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const filename = `${filenameBase}.xls`;
        triggerBrowserDownload(blob, filename);
      } else if (format === "xlsx") {
        const html = Array.isArray(rawTableData)
          ? convertToHtmlTable(rawTableData)
          : `<pre>${String(rawTableData)}</pre>`;
        blob = new Blob([html], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const filename = `${filenameBase}.xlsx`;
        triggerBrowserDownload(blob, filename);
      }
      closeMenu();
    } catch (e) {
      console.error(e);
      closeMenu();
    } finally {
      setLoading(false);
    }
  };
  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{ sx: { width: "100%", maxWidth: "1200px" } }}
    >
      <DialogTitle>
        {sourceFieldExpressionParam} ({fieldIDParam})
      </DialogTitle>
      <DialogContent>
        <TableContainer
          className="mapsExpressionTable"
          component={Paper}
          sx={{ maxHeight: 520 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header: any) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows && sortedRows.length > 0 ? (
                sortedRows.map((row: any, rowIndex: number) => (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell align="left">
                        <Box display="flex" alignItems="center" gap="8px">
                          {row.brand_id}
                          {"  "}
                          {row.brand_name}
                        </Box>
                      </TableCell>
                      <TableCell align="left">{row.report_name}</TableCell>
                      <TableCell align="left">{row.report_id}</TableCell>
                      <TableCell align="left">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {row.raw_table_id}
                          <Box>
                            <Tooltip title="Download" placement="top">
                              <IconButton
                                sx={{ width: "30px", height: "30px" }}
                                onClick={(e) => openMenu(e, row.raw_table_id)}
                              >
                                <FileDownloadIcon
                                  fontSize="small"
                                  color="secondary"
                                />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => openMenu(e, row.raw_table_id)}
                              aria-label="more"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="left">{row.config_name}</TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  <TableCell colSpan={6} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem
            onClick={() => {
              if (menuRowId == null) return;
              fetchAndDownload(menuRowId, "csv");
            }}
          >
            Download CSV
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuRowId == null) return;
              fetchAndDownload(menuRowId, "xls");
            }}
          >
            Download XLS
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuRowId == null) return;
              // Note: true .xlsx requires a library like SheetJS. We provide a best-effort fallback.
              fetchAndDownload(menuRowId, "xlsx");
            }}
          >
            Download XLSX (fallback)
          </MenuItem>
        </Menu>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="contained"
          onClick={expressionHandleClose}
          color="secondary"
        >
          close
          <CloseIcon sx={{ width: "20px", ml: "5px" }} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapsInsightExpressionModal;
